import { CalendarDays, Filter, X } from "lucide-react";
import { DashboardFilters, DashboardPeriod } from "./dashboardType";
import DatePicker from "../ui/DatePicker";
import Dropdown from "../ui/Dropdown";
import { useForm } from "react-hook-form";
import { getMasterDropdownService } from "@/services/masters";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";

interface Props {
  filters: DashboardFilters;
  updateFilter: (key: keyof DashboardFilters, value: any) => void;
  applyCustomDate: (start: string, end: string) => void;
  clearFilters: () => void;
  isSuperUser: boolean;
}

interface Option {
  id: string;
  label: string;
  value: string;
}

interface DropdownOptions {
  states: Option[];
  cities: Option[];
  zones: Option[];
  branches: Option[];
  schools: Option[];
  plans: Option[];
}

const EMPTY_OPTIONS: DropdownOptions = {
  states: [],
  cities: [],
  zones: [],
  branches: [],
  schools: [],
  plans: [],
};

interface FilterFormValues {
  start_date: string;
  end_date: string;
  State_Code: string;
  City_Code: string;
  Zone_Code: string;
  Branch_Code: string;
  School_Code: string;
  Plan_Name: string;
}

function safeArray<T>(data: T[] | null | undefined): T[] {
  return Array.isArray(data) ? data : [];
}

function pick(item: any, keys: string[]): string {
  for (const key of keys) {
    if (item[key] !== undefined && item[key] !== null && item[key] !== "") {
      return String(item[key]);
    }
  }
  return "";
}

function toOptions(list: any[], nameKeys: string[]): Option[] {
  return safeArray(list)
    .map((item) => {
      const id = pick(item, ["id", "unique_code", "code"]);
      const label = pick(item, nameKeys);
      return { id, label, value: id };
    })
    .filter((opt) => opt.id !== "" && opt.label !== "");
}

// Fields that should auto-apply the moment the user picks a value
// (mirrors old SimpleSelect behavior). Dates are excluded — those
// still go through the explicit "Apply Date" button.
const SELECT_FIELDS: (keyof FilterFormValues)[] = [
  "State_Code",
  "City_Code",
  "Zone_Code",
  "Branch_Code",
  "School_Code",
  "Plan_Name",
];

export default function FilterPanel({
  filters,
  updateFilter,
  applyCustomDate,
  clearFilters,
  isSuperUser,
}: Props) {
  const { control, watch, reset } = useForm<FilterFormValues>({
    defaultValues: {
      start_date: "",
      end_date: "",
      State_Code: filters.State_Code || "",
      City_Code: filters.City_Code || "",
      Zone_Code: filters.Zone_Code || "",
      Branch_Code: filters.Branch_Code || "",
      School_Code: filters.School_Code || "",
      Plan_Name: filters.Plan_Name || "",
    },
  });

  const watchedStart = watch("start_date");
  const watchedEnd = watch("end_date");

  const [options, setOptions] = useState<DropdownOptions>(EMPTY_OPTIONS);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const periods: { value: DashboardPeriod; label: string }[] = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "week", label: "This Week" },
    { value: "month", label: "This Month" },
    { value: "quarter", label: "This Quarter" },
    { value: "year", label: "This Year" },
    { value: "custom", label: "Custom" },
  ];

  // Fetch dropdown options once on mount.
  useEffect(() => {
    let cancelled = false;

    const fetchDropdownData = async () => {
      setLoadingOptions(true);
      try {
        const res = await getMasterDropdownService();
        if (cancelled) return;

        if (res?.status === 200) {
          const raw = res.data as any;
          setOptions({
            states: toOptions(raw?.States, ["State_Name", "Name", "name"]),
            cities: toOptions(raw?.Cities, ["City_Name", "Name", "name"]),
            zones: toOptions(raw?.Zones, ["Name", "name"]),
            branches: toOptions(raw?.Branches, ["Branch_Name", "Name", "name"]),
            schools: toOptions(raw?.Schools, ["School_Name"]),
            plans: toOptions(raw?.Plans, ["Plan_Name", "Name", "name"]),
          });
        }
      } catch {
        if (!cancelled) toast.error("Failed to load filter options");
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    };

    fetchDropdownData();
    return () => {
      cancelled = true;
    };
  }, []);

  // Propagate select changes up to the parent's filter state.
  // Using watch's subscription form (not the reactive watch() call)
  // so we only fire on actual user-driven changes, not every render.
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name && SELECT_FIELDS.includes(name as keyof FilterFormValues)) {
        updateFilter(
          name as keyof DashboardFilters,
          value[name as keyof FilterFormValues] || ""
        );
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  // Keep the form's select values in sync if filters are cleared/reset
  // externally (e.g. via "Clear" button in the parent).
  useEffect(() => {
    reset((prev) => ({
      ...prev,
      State_Code: filters.State_Code || "",
      City_Code: filters.City_Code || "",
      Zone_Code: filters.Zone_Code || "",
      Branch_Code: filters.Branch_Code || "",
      School_Code: filters.School_Code || "",
      Plan_Name: filters.Plan_Name || "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.State_Code,
    filters.City_Code,
    filters.Zone_Code,
    filters.Branch_Code,
    filters.School_Code,
    filters.Plan_Name,
  ]);

  return (
    <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-gray-600" />
          <h2 className="font-semibold text-gray-900">Dashboard Filters</h2>
        </div>
        <button type="button" onClick={clearFilters} className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-600">
          <X className="h-4 w-4" />
          Clear
        </button>
      </div>

      <div className="mb-5 flex flex-wrap gap-2">
        {periods.map((item) => (
          <button
            key={item.value}
            type="button"
            onClick={() => updateFilter("period", item.value)}
            className={`rounded-lg border px-4 py-2 text-sm font-medium transition ${
              filters.period === item.value
                ? "border-blue-600 bg-blue-600 text-white"
                : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {filters.period === "custom" && (
        <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-3">
          <DatePicker name="start_date" control={control} label="Start Date" maxDate={watchedEnd || undefined} />
          <DatePicker name="end_date" control={control} label="End Date" minDate={watchedStart || undefined} />
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => applyCustomDate(watchedStart, watchedEnd)}
              disabled={!watchedStart || !watchedEnd}
              className="w-full rounded-lg bg-blue-600 px-4 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              Apply Date
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <Dropdown
          name="State_Code"
          control={control}
          label="State"
          options={options.states}
          placeholder="All States"
        />
        <Dropdown
          name="City_Code"
          control={control}
          label="City"
          options={options.cities}
          placeholder="All Cities"
        />
        <Dropdown
          name="Zone_Code"
          control={control}
          label="Zone"
          options={options.zones}
          placeholder="All Zones"
        />
        <Dropdown
          name="Branch_Code"
          control={control}
          label="Branch"
          options={options.branches}
          placeholder="All Branches"
        />
        <Dropdown
          name="School_Code"
          control={control}
          label="School"
          options={options.schools}
          placeholder="All Schools"
        />
        <Dropdown
          name="Plan_Name"
          control={control}
          label="Plan"
          options={options.plans}
          placeholder="All Plans"
        />
      </div>

      {isSuperUser && (
        <div className="mt-4 flex items-center gap-2 border-t pt-4 text-xs text-purple-700">
          <CalendarDays className="h-4 w-4" />
          <span>Super User: all dashboard filters are available.</span>
        </div>
      )}
    </div>
  );
}